/**
 * Profession-library admin console — review LLM-minted occupation entries.
 *
 * Minted entries land status='pending' and auto-promote once PROMOTE_AT
 * distinct users hold them; this is the human path that covers what
 * auto-promotion can't: reject junk before (or after) it promotes, merge a
 * synonym into its survivor, fix a label/abbrev the model got wrong, approve
 * a good mint early. Casing typed in the Abbrev field is stored EXACTLY —
 * this is where PharmD-not-PHARMD corrections happen.
 *
 * Reject uses a two-step "click again to confirm" (no window.confirm, §0c).
 * All actions are forwarded with the staff secret + the Access email.
 */

import { PageShell } from '../components/ui/PageShell';
import { parseServerDate } from '@postopia/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { AdminShell } from '../components/AdminShell';
import {
  professionsApi,
  ProfessionAdminError,
  type ProfessionRow,
} from '../lib/professionsApi';

export const Route = createFileRoute('/professions')({
  component: ProfessionsRoute,
});

function ProfessionsRoute() {
  return (
    <AdminShell mode="feature" featureName="Professions">
      <ProfessionsConsole />
    </AdminShell>
  );
}

type StatusFilter = 'pending' | 'active' | 'rejected' | 'merged' | 'all';

function ago(iso: string): string {
  const t = parseServerDate(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return parseServerDate(iso).toLocaleDateString();
}

function ProfessionsConsole() {
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [rows, setRows] = useState<ProfessionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingReject, setPendingReject] = useState<string | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  // Per-row expandable editors: merge target + label/abbrev correction.
  const [mergeFor, setMergeFor] = useState<string | null>(null);
  const [mergeInto, setMergeInto] = useState('');
  const [editFor, setEditFor] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editAbbrev, setEditAbbrev] = useState('');

  const load = useCallback(async (qArg: string, statusArg: StatusFilter) => {
    setError(null);
    try {
      setRows(
        await professionsApi.list({
          q: qArg.trim() || undefined,
          status: statusArg,
        }),
      );
    } catch (err) {
      if (err instanceof ProfessionAdminError && err.status === 403) {
        setError('Your account does not have admin access.');
      } else if (
        err instanceof ProfessionAdminError &&
        err.code === 'professions_not_configured'
      ) {
        setError('Profession admin is not configured on this environment yet.');
      } else {
        setError("Couldn't load professions. Try again.");
      }
      setRows([]);
    }
  }, []);

  useEffect(() => {
    void load(q, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const act = useCallback(
    async (
      row: ProfessionRow,
      fn: () => Promise<unknown>,
      done: string,
    ): Promise<void> => {
      setBusyCode(row.code);
      setStatus(null);
      try {
        await fn();
        setStatus(done);
        await load(q, statusFilter);
      } catch (err) {
        setStatus(
          err instanceof ProfessionAdminError && err.code === 'invalid_target'
            ? 'Merge target must be an active, unmerged code that does not loop back.'
            : 'Action failed. Try again.',
        );
      } finally {
        setBusyCode(null);
      }
    },
    [load, q, statusFilter],
  );

  const reject = useCallback(
    (row: ProfessionRow) => {
      if (pendingReject !== row.code) {
        setPendingReject(row.code);
        return;
      }
      setPendingReject(null);
      void act(row, () => professionsApi.reject(row.code), `Rejected ${row.code}.`);
    },
    [pendingReject, act],
  );

  const input: React.CSSProperties = {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid var(--c-border-hairline)',
    background: 'var(--c-bg-page)',
    color: 'var(--c-fg-default)',
    fontSize: 14,
  };
  const btn: React.CSSProperties = {
    padding: '6px 12px',
    borderRadius: 8,
    border: '1px solid var(--c-border-strong)',
    background: 'var(--c-bg-surface)',
    color: 'var(--c-fg-default)',
    fontSize: 13,
    cursor: 'pointer',
  };

  return (
    <PageShell
      title="Professions"
      description={
        <>
          LLM-minted occupations land <strong>pending</strong> and auto-promote once enough distinct
          users hold them. Reject junk before it promotes, merge synonyms into their survivor, or fix
          a label or abbreviation. Typed casing is stored exactly (PharmD, not PHARMD).
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void load(q, statusFilter);
        }}
        style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by label, code or abbrev"
          style={{ ...input, flex: 1, minWidth: 220 }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          style={input}
        >
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="rejected">Rejected</option>
          <option value="merged">Merged</option>
          <option value="all">All</option>
        </select>
        <button type="submit" style={btn}>
          Search
        </button>
      </form>

      {status ? (
        <p style={{ fontSize: 13, color: 'var(--c-fg-secondary)', margin: '0 0 12px' }} role="status">
          {status}
        </p>
      ) : null}
      {error ? (
        <p style={{ fontSize: 14, color: '#C7563E', margin: '0 0 12px' }} role="alert">
          {error}
        </p>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows === null ? (
          <p style={{ fontSize: 14, color: 'var(--c-fg-tertiary)' }}>Loading…</p>
        ) : rows.length === 0 ? (
          <p style={{ fontSize: 14, color: 'var(--c-fg-tertiary)' }}>
            Nothing here — the queue is clear.
          </p>
        ) : (
          rows.map((row) => {
            const busy = busyCode === row.code;
            return (
              <div
                key={row.code}
                style={{
                  background: 'var(--c-bg-surface)',
                  border: '1px solid var(--c-border-hairline)',
                  borderRadius: 10,
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 15 }}>{row.label}</strong>
                  <code
                    style={{
                      fontSize: 12,
                      background: 'var(--c-bg-surface-alt)',
                      borderRadius: 4,
                      padding: '1px 6px',
                    }}
                  >
                    {row.abbrev}
                  </code>
                  <span style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>{row.code}</span>
                  <span style={{ fontSize: 12, color: 'var(--c-fg-tertiary)' }}>
                    {row.status}
                    {row.merged_into ? ` → ${row.merged_into}` : ''} · {row.source} ·{' '}
                    {row.holders} holder{row.holders === 1 ? '' : 's'} · {row.uses} save
                    {row.uses === 1 ? '' : 's'} · {ago(row.created_at)}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {row.status === 'pending' ? (
                    <button
                      style={btn}
                      disabled={busy}
                      onClick={() =>
                        void act(row, () => professionsApi.approve(row.code), `Approved ${row.code}.`)
                      }
                    >
                      Approve
                    </button>
                  ) : null}
                  {row.status !== 'rejected' ? (
                    <button
                      style={{
                        ...btn,
                        ...(pendingReject === row.code
                          ? { borderColor: '#C7563E', color: '#C7563E' }
                          : {}),
                      }}
                      disabled={busy}
                      onClick={() => reject(row)}
                    >
                      {pendingReject === row.code ? 'Click again to reject' : 'Reject'}
                    </button>
                  ) : null}
                  {row.status !== 'merged' ? (
                    <button
                      style={btn}
                      disabled={busy}
                      onClick={() => {
                        setMergeFor(mergeFor === row.code ? null : row.code);
                        setMergeInto('');
                        setEditFor(null);
                      }}
                    >
                      Merge…
                    </button>
                  ) : null}
                  <button
                    style={btn}
                    disabled={busy}
                    onClick={() => {
                      setEditFor(editFor === row.code ? null : row.code);
                      setEditLabel(row.label);
                      setEditAbbrev(row.abbrev);
                      setMergeFor(null);
                    }}
                  >
                    Edit…
                  </button>
                </div>

                {mergeFor === row.code ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!mergeInto.trim()) return;
                      setMergeFor(null);
                      void act(
                        row,
                        () => professionsApi.merge(row.code, mergeInto.trim()),
                        `Merged ${row.code} → ${mergeInto.trim()}.`,
                      );
                    }}
                    style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
                  >
                    <input
                      value={mergeInto}
                      onChange={(e) => setMergeInto(e.target.value)}
                      placeholder="Survivor code (e.g. software_engineer)"
                      style={{ ...input, flex: 1, minWidth: 200 }}
                    />
                    <button type="submit" style={btn} disabled={busy}>
                      Merge
                    </button>
                  </form>
                ) : null}

                {editFor === row.code ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      setEditFor(null);
                      void act(
                        row,
                        () =>
                          professionsApi.update(row.code, {
                            label: editLabel.trim() || undefined,
                            abbrev: editAbbrev.trim() || undefined,
                          }),
                        `Updated ${row.code}.`,
                      );
                    }}
                    style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
                  >
                    <input
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      placeholder="Label"
                      style={{ ...input, flex: 2, minWidth: 180 }}
                    />
                    <input
                      value={editAbbrev}
                      onChange={(e) => setEditAbbrev(e.target.value)}
                      placeholder="Abbrev (casing kept)"
                      style={{ ...input, flex: 1, minWidth: 120 }}
                    />
                    <button type="submit" style={btn} disabled={busy}>
                      Save
                    </button>
                  </form>
                ) : null}
              </div>
            );
          })
        )}
      </div>
    </PageShell>
  );
}
